import { useQuery } from '@apollo/client';
import { t } from '@i18n';
import { EconomicUnitBasicDetailsFragment } from '@predium/client-graphql';
import { org_sector_type_enum } from '@predium/enums';
import difference from 'lodash/difference';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import sortBy from 'lodash/sortBy';
import uniqBy from 'lodash/uniqBy';
import { useQueryStates } from 'nuqs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { SearchItemType } from '@/components/search/SearchBoxWithLazyLoading';
import { useSession } from '@/components/session/useSession';
import {
  GET_ECONOMIC_UNIT_COUNT,
  GET_ECONOMIC_UNITS_BY_IDS,
  GET_ECONOMIC_UNITS_BY_SEARCHED_STRING,
} from '@/graphql/StatusQuoAnalysis.queries';
import { SEARCH_PARAMS } from '@/routes/search-params';
import { validateUrlParameterIds } from '@/sections/StatusQuoAnalysis/utils';

export const ALL_ECONOMIC_UNITS_ITEM = (orgSector?: org_sector_type_enum) => {
  if (isNil(orgSector))
    return {
      value: -1,
      label: '',
      hasBottomDivider: true,
    };

  return {
    value: -1,
    label: t('General_AllEconomicUnits', { orgSector }),
    hasBottomDivider: true,
  };
};

export const useEconomicUnitsSelector = (debouncedSearchTerm: string, limit: number) => {
  const {
    org: { orgSector },
  } = useSession();

  const [queryParams, setQueryParams] = useQueryStates(SEARCH_PARAMS.statusQuoAnalysis.economicUnits);
  const [economicUnitIds, setEconomicUnitsIds] = useState<number[]>(queryParams?.economicUnitIds ?? []);

  useEffect(() => {
    setEconomicUnitsIds(queryParams?.economicUnitIds ?? []);
  }, [queryParams]);

  const mapToEconomicUnitSelectOption = useCallback((ecoUnit: EconomicUnitBasicDetailsFragment): SearchItemType => {
    return {
      value: ecoUnit.id,
      label: ecoUnit.name ?? '',
    };
  }, []);

  const { data: economicUnitAggregate } = useQuery(GET_ECONOMIC_UNIT_COUNT);
  const economicUnitCount = economicUnitAggregate?.economic_unit_aggregate?.aggregate?.count;

  const {
    data: economicUnitsBySearchedString,
    loading,
    fetchMore,
    refetch,
  } = useQuery(GET_ECONOMIC_UNITS_BY_SEARCHED_STRING, {
    variables: { offset: 0, limit, text: `%${debouncedSearchTerm.trim()}%` },
    notifyOnNetworkStatusChange: true,
  });

  const economicUnits = useMemo(() => {
    if (isNil(economicUnitsBySearchedString)) return [];
    const uniqueEconomicUnits = uniqBy(
      economicUnitsBySearchedString.economic_unit.map(mapToEconomicUnitSelectOption),
      'value',
    );
    uniqueEconomicUnits.unshift(ALL_ECONOMIC_UNITS_ITEM(orgSector));

    return uniqueEconomicUnits;
  }, [economicUnitsBySearchedString, mapToEconomicUnitSelectOption, orgSector]);

  const hasMoreEconomicUnits = useMemo(
    () => !isNil(economicUnitCount) && economicUnitCount > economicUnits.length - 1,
    [economicUnitCount, economicUnits],
  );

  const { data: economicUnitsByIds, loading: loadingByIds } = useQuery(GET_ECONOMIC_UNITS_BY_IDS, {
    variables: {
      ids: difference(
        economicUnitIds,
        economicUnits.map((economicUnit) => economicUnit.value),
      ),
    },
  });

  useEffect(() => {
    if (!isNil(economicUnitsByIds)) {
      const validEconomicUnitIds = economicUnitsByIds.economic_unit.map((eu) => eu.id);
      validEconomicUnitIds.unshift(ALL_ECONOMIC_UNITS_ITEM(orgSector).value);
      const allLoadedIds = [...validEconomicUnitIds, ...economicUnits.map((eu) => eu.value)];
      const validIds = validateUrlParameterIds(economicUnitIds, allLoadedIds);

      if (economicUnitIds.length > 0 && !isEqual(sortBy(validIds), sortBy(economicUnitIds))) {
        void setQueryParams({
          ...queryParams,
          economicUnitIds: validIds,
        });
      }
    }
  }, [economicUnitIds, economicUnits, economicUnitsByIds, orgSector, queryParams, setQueryParams]);

  const loadedEconomicUnits = useMemo(() => {
    if (isNil(economicUnitsByIds)) return [];
    const mappedEconomicUnits = economicUnitsByIds.economic_unit.map(mapToEconomicUnitSelectOption);
    const uniqueLoadedEconomicUnits = sortBy(uniqBy([...mappedEconomicUnits, ...economicUnits], 'value'), (item) =>
      item.label.toLocaleLowerCase(),
    );
    uniqueLoadedEconomicUnits.unshift(ALL_ECONOMIC_UNITS_ITEM(orgSector));
    return uniqueLoadedEconomicUnits;
  }, [economicUnitsByIds, mapToEconomicUnitSelectOption, economicUnits, orgSector]);

  const loadMore = useCallback(
    async (newOffset: number, newDebouncedSearchTerm: string) => {
      if (!hasMoreEconomicUnits) return;

      void fetchMore({
        variables: {
          offset: newOffset,
          limit,
          text: `%${newDebouncedSearchTerm.trim()}%`,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;
          return {
            ...fetchMoreResult,
            economic_unit: [...prev.economic_unit, ...fetchMoreResult.economic_unit],
          };
        },
      });
    },
    [hasMoreEconomicUnits, fetchMore, limit],
  );

  const selectedItems = useMemo(() => {
    if (isNil(economicUnitIds) || economicUnitIds.length === 0) return [];

    const selectedItems = economicUnitIds
      .map((id) => loadedEconomicUnits.find((ecoUnit) => ecoUnit.value === id))
      .filter((eu) => !isNil(eu));

    const firstLoadedEconomicUnitItem = loadedEconomicUnits.at(0);
    if (selectedItems.length === 0 && !isNil(firstLoadedEconomicUnitItem)) return [firstLoadedEconomicUnitItem];

    return selectedItems;
  }, [economicUnitIds, loadedEconomicUnits]);

  return {
    economicUnits,
    loadedEconomicUnits,
    economicUnitIds,
    setEconomicUnitsIds,
    loading,
    loadingByIds,
    hasMore: hasMoreEconomicUnits,
    refetch,
    loadMore,
    selectedItems,
    queryParams,
    setQueryParams,
  };
};
